import type { FocusPosition, JSONContent } from '@tiptap/react';
import type { Ref } from 'react';
import type { Control } from 'react-hook-form';

import { useMemo, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { EditorRefHandle } from '~/components';

import { RichTextEditor } from '~/components';
import { Field } from '~/components/Form';
import { mergeRefs } from '~/utils/mergeRefs';

export type ForumCommentFieldValues = {
  content: JSONContent;
};

type Props = {
  autofocus?: FocusPosition;
  control: Control<ForumCommentFieldValues>;
  ref?: Ref<EditorRefHandle | null>;
};

export const ForumCommentEditorField = ({ autofocus = false, control, ref }: Props) => {
  const editorRef = useRef<EditorRefHandle>(null);
  const mergedRef = useMemo(() => mergeRefs(editorRef, ref), [ref]);

  const { t } = useTranslation(['validation']);

  return (
    <Controller
      control={control}
      name="content"
      render={({ field: { name, onChange, ref: fieldRef, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
        <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
          <RichTextEditor
            autofocus={autofocus}
            isInvalid={invalid}
            mode="json"
            onChange={onChange}
            ref={mergeRefs(mergedRef, fieldRef)}
            value={value}
          />

          <Field.Error match={!!error}>{error?.message}</Field.Error>
        </Field.Root>
      )}
      rules={{ validate: { notEmpty: () => (editorRef.current?.isEmpty() ? t('validation:required') : true) } }}
    />
  );
};
